import { useCallback, useState, useRef, useEffect } from 'react';
import * as THREE from 'three';
import { QUANTUM_LAYOUTS, createLayoutEffects } from './layouts';
import { 
  createLayoutAnimation, 
  updateThreadCurves,
  createLayoutOptimizedCurve
} from './utils';

const useQuantumLayoutTransformer = (dependencies = {}) => {
  const [currentLayout, setCurrentLayout] = useState('default');
  const [isTransforming, setIsTransforming] = useState(false);
  const [activeLayoutEffects, setActiveLayoutEffects] = useState([]);
  const [userDefaultLayout, setUserDefaultLayout] = useState('default');
  
  // Store original positions for reset
  const originalPositionsRef = useRef(new Map());
  const originalThreadCurvesRef = useRef(new Map());
  const userDefaultPositionsRef = useRef(null); // Store user's default positions
  
  // Destructure dependencies
  const {
    sceneRef,
    departmentsRef,
    threadsRef,
    departmentsData = [],
    threadsData = [],
    createQuantumBloomEffect = () => {},
    createQuantumWowMoment = () => {},
    selectedThreads = [],
    safeOperation = (fn) => fn()
  } = dependencies;

  // ========== CORE TRANSFORMATION ENGINE ==========
  
  // Save original positions on first mount
  useEffect(() => {
    if (departmentsRef?.current && originalPositionsRef.current.size === 0) {
      const positions = new Map();
      
      departmentsRef.current.forEach((dept, id) => {
        if (dept?.position) {
          positions.set(id, {
            x: dept.position.x,
            y: dept.position.y,
            z: dept.position.z
          });
        }
      });
      
      originalPositionsRef.current = positions;
      
      // Save original thread curves
      if (threadsRef?.current) {
        const curves = new Map();
        threadsRef.current.forEach((threadObj, threadId) => {
          if (threadObj?.group) {
            const threadCurves = [];
            threadObj.group.children.forEach(child => {
              if (child.userData?.type === 'thread-connection' && child.userData.curve) {
                threadCurves.push({
                  start: child.userData.startDept?.id,
                  end: child.userData.endDept?.id,
                  controlPoints: child.userData.curve.getPoints(10).map(p => ({x: p.x, y: p.y, z: p.z}))
                });
              }
            });
            curves.set(threadId, threadCurves);
          }
        });
        originalThreadCurvesRef.current = curves;
      }
    }
  }, [departmentsRef, threadsRef]);

  // Calculate layout positions
  const calculateLayoutPositions = useCallback((layoutId, selectedThreadIds = []) => {
    const layout = QUANTUM_LAYOUTS[layoutId];
    if (!layout) return { departmentPositions: new Map(), threadPositions: new Map() };
    
    // Group departments by type
    const departmentsByType = {
      function: [],
      domain: [],
      system: []
    };
    
    departmentsData.forEach(dept => {
      if (departmentsByType[dept.type]) {
        departmentsByType[dept.type].push(dept);
      }
    });
    
    // Calculate department positions
    const departmentPositions = new Map();
    
    Object.entries(departmentsByType).forEach(([type, depts]) => {
      const positions = layout.calculatePositions(depts, type, selectedThreadIds);
      depts.forEach((dept, idx) => {
        if (positions[idx]) {
          departmentPositions.set(dept.id, positions[idx]);
        }
      });
    });
    
    // Calculate thread midpoint positions
    const threadPositions = new Map();
    
    threadsData.forEach(thread => {
      const connectedDepts = departmentsData.filter(dept => 
        (thread.connects && thread.connects.includes(dept.name)) ||
        (thread.domains && thread.domains.includes(dept.name)) ||
        (thread.systems && thread.systems.includes(dept.name))
      );
      
      if (connectedDepts.length > 0) {
        let avgX = 0, avgY = 0, avgZ = 0;
        let count = 0;
        
        connectedDepts.forEach(dept => {
          const pos = departmentPositions.get(dept.id);
          if (pos) {
            avgX += pos[0];
            avgY += pos[1];
            avgZ += pos[2];
            count++;
          }
        });
        
        if (count > 0) {
          threadPositions.set(thread.id, [
            avgX / count,
            avgY / count + 10,
            avgZ / count
          ]);
        }
      }
    });
    
    return { departmentPositions, threadPositions };
  }, [departmentsData, threadsData]);

  // Update thread connections
  const updateAllThreadConnections = useCallback(async (departmentPositions, layoutId, selectedThreadIds = []) => {
    return new Promise((resolve) => {
      if (!threadsRef?.current || !departmentsRef?.current) {
        resolve();
        return;
      }
      
      let updatedCount = 0;
      const totalThreads = threadsRef.current.size;
      
      threadsRef.current.forEach((threadObj, threadId) => {
        if (threadObj?.group) {
          // Remove old connections
          const oldConnections = [];
          threadObj.group.children.forEach(child => {
            if (child.userData?.type === 'thread-connection') {
              oldConnections.push(child);
            }
          });
          
          oldConnections.forEach(child => {
            threadObj.group.remove(child);
            if (child.geometry) child.geometry.dispose();
            if (child.material) child.material.dispose();
          });
          
          // Find thread data
          const thread = threadsData.find(t => t.id === threadId);
          if (!thread) return;
          
          // Find connected departments
          const connectedDepts = departmentsData.filter(dept => 
            (thread.connects && thread.connects.includes(dept.name)) ||
            (thread.domains && thread.domains.includes(dept.name)) ||
            (thread.systems && thread.systems.includes(dept.name))
          );
          
          // Create connections
          for (let i = 0; i < connectedDepts.length - 1; i++) {
            const startDept = departmentsRef.current.get(connectedDepts[i].id);
            const endDept = departmentsRef.current.get(connectedDepts[i + 1].id);
            
            if (startDept && endDept) {
              const curve = createLayoutOptimizedCurve(
                [startDept.position.x, startDept.position.y, startDept.position.z],
                [endDept.position.x, endDept.position.y, endDept.position.z],
                layoutId,
                thread,
                selectedThreadIds.includes(threadId)
              );
              
              if (curve) {
                const points = curve.getPoints(50);
                const geometry = new THREE.BufferGeometry().setFromPoints(points);
                const isSelected = selectedThreadIds.includes(threadId);
                
                const material = new THREE.LineBasicMaterial({
                  color: isSelected ? 0x00ff00 : (threadObj.isGolden ? 0xffd700 : (thread.color || 0x45b7d1)),
                  transparent: true,
                  opacity: isSelected ? 0.9 : (thread.critical ? 0.8 : 0.6),
                  linewidth: isSelected ? 4 : (thread.critical ? 3 : 2)
                });
                
                const line = new THREE.Line(geometry, material);
                line.userData = {
                  type: 'thread-connection',
                  startDept: { id: connectedDepts[i].id, ...connectedDepts[i] },
                  endDept: { id: connectedDepts[i + 1].id, ...connectedDepts[i + 1] },
                  curve: curve,
                  threadData: thread,
                  isSelected: isSelected
                };
                
                threadObj.group.add(line);
              }
            }
          }
          
          updatedCount++;
        }
      });
      
      const checkCompletion = () => {
        if (updatedCount >= totalThreads) {
          resolve();
        } else {
          setTimeout(checkCompletion, 100);
        }
      };
      checkCompletion();
    });
  }, [threadsData, departmentsData, departmentsRef, threadsRef]);

  // ========== MAIN TRANSFORMATION FUNCTION ==========
  const transformToLayout = useCallback(async (layoutId, selectedThreadIds = []) => {
    if (isTransforming || !sceneRef?.current || !departmentsRef?.current) {
      console.warn('Cannot transform layout - system not ready');
      return false;
    }
    
    const layout = QUANTUM_LAYOUTS[layoutId];
    if (!layout) {
      console.error(`Layout ${layoutId} not found`);
      return false;
    }
    
    setIsTransforming(true);
    console.log(`🌀 Transforming to ${layout.name} layout...`);
    
    try {
      // 1. PRE-TRANSFORMATION EFFECTS
      createQuantumBloomEffect(2.5, 500);
      
      // 2. CALCULATE POSITIONS
      const { departmentPositions, threadPositions } = calculateLayoutPositions(layoutId, selectedThreadIds);
      
      if (departmentPositions.size === 0) {
        throw new Error('No positions calculated for transformation');
      }
      
      // 3. ANIMATE MOVEMENT
      await new Promise((resolve) => {
        const startTime = Date.now();
        const duration = 1500;
        const originalPositions = new Map();
        
        departmentsRef.current.forEach((dept, id) => {
          if (dept?.position) {
            originalPositions.set(id, dept.position.clone());
          }
        });
        
        const animate = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const easeProgress = 1 - Math.pow(1 - progress, 3);
          
          departmentsRef.current.forEach((dept, id) => {
            const original = originalPositions.get(id);
            const targetArray = departmentPositions.get(id);
            
            if (dept?.position && original && targetArray) {
              const target = new THREE.Vector3(targetArray[0], targetArray[1], targetArray[2]);
              dept.position.lerpVectors(original, target, easeProgress);
              
              // Scale effect
              const scale = 1 + Math.sin(progress * Math.PI) * 0.3;
              dept.scale.setScalar(scale);
            }
          });
          
          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            resolve();
          }
        };
        
        animate();
      });
      
      // 4. UPDATE THREAD CONNECTIONS
      await updateAllThreadConnections(departmentPositions, layoutId, selectedThreadIds);
      
      // 5. APPLY VISUAL EFFECTS
/*      activeLayoutEffects.forEach(effect => {
        if (effect?.parent) {
          effect.parent.remove(effect);
          if (effect.geometry) effect.geometry.dispose();
          if (effect.material) {
            if (Array.isArray(effect.material)) {
              effect.material.forEach(m => m.dispose());
            } else {
              effect.material.dispose();
            }
          }
        }
      });

      if (createLayoutEffects[layoutId]) {
        const effects = createLayoutEffects[layoutId](sceneRef.current, selectedThreadIds);
        setActiveLayoutEffects([effects]);
      }
       */     
	  await new Promise(resolve => {
  setTimeout(() => {
    updateThreadLabelsAndFlow(threadPositions, layoutId);
    resolve();
  }, 100);
});
      // 6. FINAL EFFECTS
      createQuantumWowMoment();
      createQuantumBloomEffect(3.0, 800);
      
      // 7. UPDATE STATE
      setCurrentLayout(layoutId);
      
      console.log(`✅ Successfully transformed to ${layout.name} layout!`);
      console.log(`   - Moved ${departmentPositions.size} departments`);
      console.log(`   - Updated ${threadsRef.current.size} thread connections`);
      
      return true;
      
    } catch (error) {
      console.error('Layout transformation failed:', error);
      return false;
    } finally {
      setIsTransforming(false);
    }
  }, [
    isTransforming, 
    sceneRef, 
    departmentsRef, 
    calculateLayoutPositions,
    updateAllThreadConnections,
    createQuantumBloomEffect,
    createQuantumWowMoment,
    activeLayoutEffects
  ]);

// ========== UPDATE THREAD LABELS AND FLOW ELEMENTS ==========
const updateThreadLabelsAndFlow = useCallback((threadPositions, layoutId) => {
  if (!threadsRef?.current || !sceneRef?.current) return;

  threadsRef.current.forEach((threadObj, threadId) => {
    if (!threadObj?.group) return;

    const targetPosition = threadPositions.get(threadId);
    if (!targetPosition) return;

    // Update thread labels
    threadObj.group.children.forEach(child => {
      if (child.userData?.type === 'thread-label' || 
          child.userData?.type === 'thread-glow' ||
          child.userData?.type === 'data-particle' ||
          child.userData?.type === 'sequence-number' ||
          child.userData?.type === 'flow-element') {
        
        // Position relative to thread center
        const offset = child.userData.offset || { x: 0, y: 0, z: 0 };
        child.position.set(
          targetPosition[0] + offset.x,
          targetPosition[1] + offset.y + 5, // Add height for labels
          targetPosition[2] + offset.z
        );
        
        // Layout-specific adjustments
        switch(layoutId) {
          case 'galacticSpiral':
            // Rotate labels to face outward
            child.lookAt(0, 0, 0);
            break;
          case 'solarSystem':
            // Keep labels upright
            child.rotation.set(0, 0, 0);
            break;
        }
      }
    });

    // Update any sequence numbers associated with the thread
    updateThreadSequenceNumbers(threadId, targetPosition, layoutId);
  });
}, [threadsRef, sceneRef]);

// Helper function to update sequence numbers
const updateThreadSequenceNumbers = useCallback((threadId, position, layoutId) => {
  if (!sceneRef?.current) return;

  // Find all sequence numbers for this thread
  sceneRef.current.traverse((child) => {
    if (child.userData?.threadId === threadId && 
        (child.userData?.type === 'sequence-number' || 
         child.userData?.type === 'flow-element')) {
      
      // Calculate new position relative to thread
      const offset = child.userData.originalOffset || { x: 0, y: 0, z: 0 };
      
      // Apply layout-specific positioning
      let newPosition;
      switch(layoutId) {
        case 'quantumCloud':
          // Random cloud-like distribution
          newPosition = [
            position[0] + offset.x + (Math.random() - 0.5) * 8,
            position[1] + offset.y + 3,
            position[2] + offset.z + (Math.random() - 0.5) * 8
          ];
          break;
        case 'neuralNetwork':
          // Clustered around thread
          newPosition = [
            position[0] + offset.x * 0.7,
            position[1] + offset.y + 2,
            position[2] + offset.z * 0.7
          ];
          break;
        case 'galacticSpiral':
          // Follow spiral pattern
          const angle = child.userData.sequenceIndex * 0.5;
          newPosition = [
            position[0] + Math.cos(angle) * 6,
            position[1] + offset.y + 4,
            position[2] + Math.sin(angle) * 6
          ];
          break;
        default:
          newPosition = [
            position[0] + offset.x,
            position[1] + offset.y + 5,
            position[2] + offset.z
          ];
      }

      // Animate movement
      const originalPos = child.position.clone();
      const targetPos = new THREE.Vector3(...newPosition);
      
      // Animate over time
      const startTime = Date.now();
      const duration = 1000;
      
      const animateMove = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeProgress = 1 - Math.pow(1 - progress, 3);
        
        child.position.lerpVectors(originalPos, targetPos, easeProgress);
        
        if (progress < 1) {
          requestAnimationFrame(animateMove);
        }
      };
      
      animateMove();
    }
  });
}, [sceneRef]);


  // ========== RESET FUNCTIONS ==========
  const resetToDefaultLayout = useCallback(async () => {
    console.log('🔄 Resetting to default layout...');
    
    if (!departmentsRef?.current) {
      console.warn('Cannot reset - departments reference not available');
      return;
    }
    
    setIsTransforming(true);
    
    try {
      // Remove layout effects
      activeLayoutEffects.forEach(effect => {
        if (effect?.parent) {
          effect.parent.remove(effect);
        }
      });
      setActiveLayoutEffects([]);
      
      // Determine which positions to reset to
      const targetPositions = userDefaultPositionsRef.current || originalPositionsRef.current;
      
      // Animate back to positions
      await new Promise((resolve) => {
        const startTime = Date.now();
        const duration = 1500;
        const currentPositions = new Map();
        
        departmentsRef.current.forEach((dept, id) => {
          if (dept?.position) {
            currentPositions.set(id, dept.position.clone());
          }
        });
        
        const animate = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const easeProgress = 1 - Math.pow(1 - progress, 3);
          
          departmentsRef.current.forEach((dept, id) => {
            const current = currentPositions.get(id);
            const target = targetPositions.get(id);
            
            if (dept?.position && current && target) {
              const targetVec = new THREE.Vector3(target.x, target.y, target.z);
              dept.position.lerpVectors(current, targetVec, easeProgress);
            }
          });
          
          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            resolve();
          }
        };
        
        animate();
      });
      
      // Reset thread connections
const positionsMap = new Map();
targetPositions.forEach((pos, id) => {
  positionsMap.set(id, [pos.x, pos.y, pos.z]);
});

await updateAllThreadConnections(positionsMap, userDefaultLayout);

// NEW: Reset thread labels and flow elements
// Calculate thread positions for reset
const { threadPositions: resetThreadPositions } = calculateLayoutPositions(userDefaultLayout, []);
updateThreadLabelsAndFlow(resetThreadPositions, userDefaultLayout);

setCurrentLayout(userDefaultLayout);

      createQuantumBloomEffect(2.0, 600);
      
      console.log('✅ Reset to default layout complete');
      
    } catch (error) {
      console.error('Reset failed:', error);
    } finally {
      setIsTransforming(false);
    }
  }, [departmentsRef, activeLayoutEffects, updateAllThreadConnections, createQuantumBloomEffect, userDefaultLayout]);

  // ========== SET AS DEFAULT FUNCTION ==========
  const setAsDefaultLayout = useCallback(async () => {
    console.log('⭐ Setting current layout as default...');
    
    if (!departmentsRef?.current) {
      console.warn('Cannot set default - departments reference not available');
      return false;
    }
    
    // Save current positions as user default
    const currentPositions = new Map();
    departmentsRef.current.forEach((dept, id) => {
      if (dept?.position) {
        currentPositions.set(id, {
          x: dept.position.x,
          y: dept.position.y,
          z: dept.position.z
        });
      }
    });
    
    userDefaultPositionsRef.current = currentPositions;
    setUserDefaultLayout(currentLayout);
    
    // Save to localStorage for persistence
    try {
      const layoutData = {
        layoutId: currentLayout,
        positions: Array.from(currentPositions.entries()),
        timestamp: Date.now()
      };
      localStorage.setItem('quantumLayoutDefault', JSON.stringify(layoutData));
    } catch (error) {
      console.warn('Could not save to localStorage:', error);
    }
    
    console.log(`✅ Set "${currentLayout}" as default layout`);
    createQuantumBloomEffect(1.5, 300);
    
    return true;
  }, [departmentsRef, currentLayout, createQuantumBloomEffect]);

  // ========== LOAD SAVED DEFAULT ==========
  useEffect(() => {
    // Load saved default from localStorage
    try {
      const savedData = localStorage.getItem('quantumLayoutDefault');
      if (savedData) {
        const { layoutId, positions } = JSON.parse(savedData);
        if (layoutId && positions) {
          const savedPositions = new Map(positions);
          userDefaultPositionsRef.current = savedPositions;
          setUserDefaultLayout(layoutId);
          console.log(`📂 Loaded saved default layout: ${layoutId}`);
        }
      }
    } catch (error) {
      console.warn('Could not load saved layout:', error);
    }
  }, []);

  return {
    currentLayout,
    isTransforming,
    transformToLayout,
    resetToDefaultLayout,
    setAsDefaultLayout, // Make sure this is returned
    availableLayouts: QUANTUM_LAYOUTS
  };
};

export default useQuantumLayoutTransformer;